/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect, useState } from "react";
import { Box, Dialog, DialogContent, FormControl } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { StyledSwitch } from "../total_sales_card/styles";
import { StyledMenuItem } from "../styled_menu_item";
import { SelectedMenuItem } from "../selected_menu_item";
import { DropdownInfiniteLoader } from "../dropdown_infinite_loader";
import { ModalCancelButton } from "../modal_cancel_button";
import { LoadingSpinner } from "../loading_spinner";
import { Loader } from "../loader";
import DropdownSearchBar from "../dorpdown_searchbar";
import {
  ActivePolicyCheckText,
  ActivityChip,
  ActivityChipCheckbox,
  ActivityChipGroup,
  AddRuleButton,
  CloseIconButton,
  CreateButton,
  DraftBanner,
  DraftHelperText,
  DraftIconWrapper,
  DraftTitle,
  dropdownMenuProps,
  ErrorText,
  FieldContainer,
  FieldLabel,
  FixedActionContainer,
  HelperText,
  InactivityFieldLabel,
  InactivityHeader,
  InactivityPeriodContainer,
  InactivityPeriodRow,
  InactivityPeriodUnit,
  InactivityRuleCard,
  InactivityRuleCardHeader,
  InactivitySection,
  LoadingPolicyDialogPaperProps,
  ModalLoaderContainer,
  PolicyDialogPaperProps,
  PolicyTypeGroup,
  PolicyTypeOption,
  RuleControlContainer,
  RuleIconWrapper,
  RuleLabel,
  RuleLabelContainer,
  RequiredMark,
  RuleRow,
  RuleStatus,
  scrollableContent,
  SectionContainer,
  SectionHelperText,
  SectionTitle,
  SelectPlaceholder,
  StyledSelect,
  StyledTextField,
  TitleContainer,
  TitleText,
  ToggleValueText,
} from "./styles";

export const POLICY_TYPE_OPTIONS = [
  { label: "Group", value: "group" },
  { label: "Restaurant", value: "restaurant" },
];

// `verification_method` names the population a policy governs. `manual` is the
// only value the backend whitelists today, so the control is pre-set and locked.
export const APPLIES_TO_OPTIONS = [{ label: "Manual", value: "manual" }];

export const NOTIFICATION_RECIPIENT_OPTIONS = [
  { label: "Email to admin", value: "admin" },
  { label: "Email to employee/guest", value: "user" },
  { label: "Email to both", value: "all" },
];

export const VERIFICATION_FREQUENCY_OPTIONS = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Annually", value: "annually" },
];

export const INACTIVITY_CHECK_OPTIONS = [
  { label: "Daily login", value: "last_login" },
  { label: "Active discount card", value: "active_discount_card" },
  { label: "Has Check-In", value: "check_in" },
];

// Mirrors CardPolicyRule::VERIFICATION_FREQUENCY_DAYS. The grace period has to
// fit inside a single verification cycle, so the check runs client-side too
// instead of waiting for the 422. See describeGracePeriodError.
const VERIFICATION_FREQUENCY_CYCLE_DAYS = {
  daily: 1,
  weekly: 7,
  monthly: 30,
  quarterly: 90,
  annually: 365,
};

const DEFAULT_INACTIVITY_CHECKS = ["last_login"];
const DEFAULT_INACTIVITY_PERIOD_DAYS = 15;

/**
 * Single source of truth for the policy form. The same shape backs the create
 * and the update flow, so every control reads and writes one state object that a
 * fetched policy can be mapped onto (see mapPolicyToConfiguration).
 */
export const INITIAL_POLICY_CONFIGURATION = {
  draft: false,
  name: "",
  policy_type: "group",
  restaurant_group_id: "",
  restaurant_ids: [],
  description: "",
  // Edit-mode only. `status` is the policy's persisted status, kept so the draft
  // toggle can tell an actual status change from a no-op; `scope_name` is the
  // already-resolved name of the locked scope; `persisted_rules` is the rule set
  // the policy was loaded with, which an update diffs against.
  status: "",
  scope_name: "",
  persisted_rules: [],
  // Effective rules. An empty string / empty array / null means "not set", which
  // is what keeps the rule out of the payload so the parent policy is inherited.
  verification_method: "manual",
  notification_recipients: "",
  verification_frequency: "",
  max_active_users: "",
  exceptions_permitted: null,
  exception_network_guest_ids: [],
  grace_period_days: "",
  inactivity_rule_enabled: false,
  inactivity_checks: [],
  inactivity_period_days: "",
};

const sanitizeIntegerInput = (value) => `${value ?? ""}`.replace(/[^0-9]/g, "");

const isIntegerFieldSet = (value) => `${value ?? ""}`.trim() !== "";

/**
 * The endpoint requires all five inactivity-review rules together or none at all,
 * so the asterisks appear across the whole group the moment any one is set.
 */
export const INACTIVITY_REVIEW_FIELDS = [
  "notification_recipients",
  "verification_frequency",
  "grace_period_days",
  "inactivity_checks",
  "inactivity_period_days",
];

/**
 * @description Whether the author has set any inactivity-review rule, which makes
 * every rule in that group mandatory.
 * @param policyConfigurationData The current form state.
 * @returns True when at least one of the five review rules carries a value.
 */
export const hasAnyInactivityReviewField = (policyConfigurationData) =>
  INACTIVITY_REVIEW_FIELDS.some((field) => {
    const value = policyConfigurationData?.[field];

    return Array.isArray(value) ? value.length > 0 : isIntegerFieldSet(value);
  });

/**
 * @description Builds the `rules` array for the policy engine create endpoint,
 * omitting every rule the author left unset so it inherits from the parent.
 * @param policyConfigurationData The current form state.
 * @returns The rules array accepted by the policy engine endpoint.
 */
export const buildPolicyRules = (policyConfigurationData) => {
  const rules = [
    {
      rule_key: "verification_method",
      rule_value: policyConfigurationData?.verification_method || "manual",
    },
  ];

  if (policyConfigurationData?.notification_recipients) {
    rules.push({
      rule_key: "notification_recipients",
      rule_value: policyConfigurationData.notification_recipients,
    });
  }

  if (policyConfigurationData?.verification_frequency) {
    rules.push({
      rule_key: "verification_frequency",
      rule_value: policyConfigurationData.verification_frequency,
    });
  }

  if (isIntegerFieldSet(policyConfigurationData?.max_active_users)) {
    rules.push({
      rule_key: "max_active_users",
      rule_value: Number(policyConfigurationData.max_active_users),
    });
  }

  if (policyConfigurationData?.exceptions_permitted !== null && policyConfigurationData?.exceptions_permitted !== undefined) {
    rules.push({
      rule_key: "exceptions_permitted",
      rule_value: Boolean(policyConfigurationData.exceptions_permitted),
    });
  }

  // The switch and the list are one setting split over two rules; the backend
  // rejects either without the other, so the list ships only while the switch is on.
  if (policyConfigurationData?.exceptions_permitted === true) {
    rules.push({
      rule_key: "exception_network_guest_ids",
      rule_value: policyConfigurationData?.exception_network_guest_ids || [],
    });
  }

  if (isIntegerFieldSet(policyConfigurationData?.grace_period_days)) {
    rules.push({
      rule_key: "grace_period_days",
      rule_value: Number(policyConfigurationData.grace_period_days),
    });
  }

  // `inactivity_checks` and `inactivity_period_days` are one setting split over
  // two rules; the backend rejects a half-configured pair, so they ship together.
  if (policyConfigurationData?.inactivity_rule_enabled) {
    rules.push({
      rule_key: "inactivity_checks",
      rule_value: policyConfigurationData?.inactivity_checks || [],
    });
    rules.push({
      rule_key: "inactivity_period_days",
      rule_value: Number(policyConfigurationData?.inactivity_period_days),
    });
  }

  return rules;
};

/**
 * @description Builds the full create-policy request body from the form state.
 * @param policyConfigurationData The current form state.
 * @returns The payload for POST /v1/admin/policy_engines.
 */
export const buildPolicyPayload = (policyConfigurationData) => {
  const isRestaurantPolicy = policyConfigurationData?.policy_type === "restaurant";

  return {
    // Only the scope key that matches the policy type is sent. A restaurant policy
    // derives its group from the target restaurants server-side, and sending a
    // group alongside them makes the backend cross-check the two — which a stale
    // value from a since-hidden filter would fail.
    restaurant_group_id: isRestaurantPolicy ? undefined : policyConfigurationData?.restaurant_group_id,
    policy_type: policyConfigurationData?.policy_type,
    name: `${policyConfigurationData?.name || ""}`.trim(),
    description: `${policyConfigurationData?.description || ""}`.trim() || undefined,
    draft: Boolean(policyConfigurationData?.draft),
    restaurant_ids: isRestaurantPolicy ? policyConfigurationData?.restaurant_ids : undefined,
    rules: buildPolicyRules(policyConfigurationData),
  };
};

// Maps a persisted rule onto the form fields that represent it. Values arrive as
// their JSON types; the number fields are text inputs, so they are stringified.
const RULE_CONFIGURATION_MAPPERS = {
  verification_method: (ruleValue) => ({ verification_method: ruleValue || "manual" }),
  notification_recipients: (ruleValue) => ({ notification_recipients: ruleValue || "" }),
  verification_frequency: (ruleValue) => ({ verification_frequency: ruleValue || "" }),
  max_active_users: (ruleValue) => ({ max_active_users: `${ruleValue ?? ""}` }),
  exceptions_permitted: (ruleValue) => ({ exceptions_permitted: Boolean(ruleValue) }),
  exception_network_guest_ids: (ruleValue) => ({
    exception_network_guest_ids: Array.isArray(ruleValue) ? ruleValue : [],
  }),
  grace_period_days: (ruleValue) => ({ grace_period_days: `${ruleValue ?? ""}` }),
  inactivity_checks: (ruleValue) => ({
    inactivity_rule_enabled: true,
    inactivity_checks: Array.isArray(ruleValue) ? ruleValue : [],
  }),
  inactivity_period_days: (ruleValue) => ({
    inactivity_rule_enabled: true,
    inactivity_period_days: `${ruleValue ?? ""}`,
  }),
};

// hasOwnProperty, not a bare lookup: a rule_key colliding with an inherited
// Object member ("constructor") would otherwise resolve to a truthy non-mapper.
const isFormManagedRule = (rule) => (
  Object.prototype.hasOwnProperty.call(RULE_CONFIGURATION_MAPPERS, `${rule?.rule_key}`)
);

/** Drops the ids the show endpoint returns, leaving only what an update sends. */
const toRulePairs = (rules) => (
  (Array.isArray(rules) ? rules : []).map((rule) => ({ rule_key: rule?.rule_key, rule_value: rule?.rule_value }))
);

/**
 * @description Order-insensitive signature of a rule set, used to tell an edited
 * rule set from an untouched one. Rule keys are unique per policy, so sorting by
 * key is enough to make two equivalent sets compare equal.
 * @param rules A rule set.
 * @returns A string that is equal for equivalent rule sets.
 */
const canonicalizeRules = (rules) => JSON.stringify(
  toRulePairs(rules).sort((left, right) => `${left.rule_key}`.localeCompare(`${right.rule_key}`))
);

/**
 * @description The rule set a submit would write: everything this form owns, plus
 * the persisted rules it has no control for — `rules` replaces the whole set, so
 * those have to ride along or the update would delete them.
 * @param policyConfigurationData The current form state.
 * @returns The rule set that would be sent.
 */
const buildNextRuleSet = (policyConfigurationData) => [
  ...buildPolicyRules(policyConfigurationData),
  ...toRulePairs(policyConfigurationData?.persisted_rules).filter((rule) => !isFormManagedRule(rule)),
];

/**
 * @description Whether this submit would replace the persisted rule set. An update
 * that leaves every rule alone sends no `rules`, and the endpoint skips every
 * cross-rule check in that case (`rules_provided?`) — so the form has to skip them
 * too, or a policy already holding an incomplete rule set could never be renamed.
 * Always true for a create, which has no persisted rules to match.
 * @param policyConfigurationData The current form state.
 * @returns True when the rule set is being written.
 */
export const replacesPolicyRuleSet = (policyConfigurationData) => (
  canonicalizeRules(buildNextRuleSet(policyConfigurationData))
    !== canonicalizeRules(policyConfigurationData?.persisted_rules)
);

/**
 * @description Maps a policy fetched from the show endpoint onto the form state,
 * so the edit modal opens on exactly what is persisted. The rule set is kept
 * verbatim as `persisted_rules` for the update to diff against.
 * @param policy The policy returned by GET /v1/admin/policy_engines/:id.
 * @param scopeName Display name of the policy's scope, already resolved by the listing.
 * @returns Form state for PolicyEngineConfigurationModal.
 */
export const mapPolicyToConfiguration = (policy, scopeName = "") => {
  const rules = Array.isArray(policy?.rules) ? policy.rules : [];

  const ruleFields = rules.reduce((accumulator, rule) => {
    const mapRule = isFormManagedRule(rule) ? RULE_CONFIGURATION_MAPPERS[rule.rule_key] : null;
    return mapRule ? { ...accumulator, ...mapRule(rule?.rule_value) } : accumulator;
  }, {});

  return {
    ...INITIAL_POLICY_CONFIGURATION,
    draft: policy?.status === "draft",
    status: policy?.status || "",
    name: policy?.name || "",
    policy_type: policy?.policy_type || "group",
    restaurant_group_id: policy?.restaurant_group_id || "",
    restaurant_ids: policy?.restaurant_id ? [policy.restaurant_id] : [],
    description: policy?.description || "",
    scope_name: scopeName || "",
    persisted_rules: toRulePairs(rules),
    ...ruleFields,
  };
};

/**
 * @description Builds the update-policy request body from the form state. Scope
 * and policy type are absent by design: the endpoint does not accept them, so an
 * existing policy keeps the scope it was created with.
 * @param policyConfigurationData The current form state.
 * @returns The payload for PUT /v1/admin/policy_engines/:id.
 */
export const buildPolicyUpdatePayload = (policyConfigurationData) => {
  const isDraft = Boolean(policyConfigurationData?.draft);
  const wasDraft = policyConfigurationData?.status === "draft";

  return {
    name: `${policyConfigurationData?.name || ""}`.trim(),
    description: `${policyConfigurationData?.description || ""}`.trim(),
    // Only sent when the toggle actually moved. Deriving it unconditionally would
    // activate an archived policy the moment anything else on it was edited.
    status: isDraft === wasDraft ? undefined : (isDraft ? "draft" : "active"),
    // Omitted when the author did not touch a rule: supplying `rules` makes the
    // endpoint re-validate the effective cap against live headcount, which would
    // otherwise let count drift block an edit that only renamed the policy.
    rules: replacesPolicyRuleSet(policyConfigurationData)
      ? buildNextRuleSet(policyConfigurationData)
      : undefined,
  };
};

/**
 * The label each required field is shown under, so a message names the control
 * the author has to go back to rather than the rule_key behind it. "Verification
 * method" is the notification_recipients row; the verification_method rule is the
 * locked "Applies to" control, which can never be blank.
 */
const POLICY_FIELD_LABELS = {
  name: "Policy name",
  restaurant_group_id: "Restaurant group",
  restaurant_ids: "Restaurant",
  notification_recipients: "Verification method",
  verification_frequency: "Verification frequency",
  grace_period_days: "Grace period (days)",
  inactivity_checks: "Inactivity rules",
  inactivity_period_days: "Inactivity period",
  exception_network_guest_ids: "Excepted users",
};

/**
 * @description Every required field the author has left blank, named by its form
 * label. Mirrors the asterisks the modal renders — the scope key for the chosen
 * policy type, the five inactivity-review rules once any one of them carries a
 * value, and the excepted users once exceptions are switched on — so the endpoint
 * is never asked to reject a rule set the form could already tell was incomplete.
 * @param policyConfigurationData The current form state.
 * @returns The blank required field names, in the order the form shows them.
 */
export const collectMissingRequiredPolicyFields = (policyConfigurationData) => {
  const isFieldSet = (field) => {
    const value = policyConfigurationData?.[field];

    return Array.isArray(value) ? value.length > 0 : isIntegerFieldSet(value);
  };

  // The rule requirements are cross-rule checks, which the endpoint only applies to
  // a rule set it is actually being sent. Applying them to an untouched one would
  // strand a policy that already holds an incomplete set — it could not even be
  // renamed. Name and scope are not rule checks, so they always apply.
  const ruleFieldsRequired = replacesPolicyRuleSet(policyConfigurationData);

  const requiredFields = [
    "name",
    // Each policy type requires only its own scope key — the other filter is hidden.
    policyConfigurationData?.policy_type === "restaurant" ? "restaurant_ids" : "restaurant_group_id",
    // The review rules are all-or-nothing, so one of them being set makes the rest
    // mandatory — the same condition the asterisks render on.
    ...(ruleFieldsRequired && hasAnyInactivityReviewField(policyConfigurationData)
      ? INACTIVITY_REVIEW_FIELDS
      : []),
    ...(ruleFieldsRequired && policyConfigurationData?.exceptions_permitted === true
      ? ["exception_network_guest_ids"]
      : []),
  ];

  return requiredFields.filter((field) => !isFieldSet(field));
};

/**
 * The only fields that still render a message beside them. Every rule control
 * signals its problem with an outline instead, so a rule message that the missing
 * -field list does not already name has to reach the snackbar or it would be lost.
 */
const INLINE_MESSAGE_FIELDS = ["name", "restaurant_group_id", "restaurant_ids"];

/**
 * @description The blank required fields named by their form label, for a message
 * that has to read as a sentence rather than drive a control.
 * @param policyConfigurationData The current form state.
 * @returns The labels of the blank required fields, in the order the form shows them.
 */
export const describeMissingPolicyFields = (policyConfigurationData) => (
  collectMissingRequiredPolicyFields(policyConfigurationData).map((field) => POLICY_FIELD_LABELS[field])
);

/**
 * @description The message for a grace period outside the window its verification
 * cycle allows. Mirrors RuleValidatable#validate_grace_period_within_verification_cycle:
 * at least a day, since 0 mints a link that is already expired when it arrives,
 * and at most one whole cycle, since only the newest cycle is ever surfaced.
 * @param gracePeriodDays The entered grace period, or null when left blank.
 * @param frequency The selected verification frequency.
 * @returns The error message, or null when the grace period is in range.
 */
const describeGracePeriodError = (gracePeriodDays, frequency) => {
  if (gracePeriodDays === null) {
    return null;
  }

  const cycleDays = VERIFICATION_FREQUENCY_CYCLE_DAYS[frequency];

  // No frequency picked yet, so there is no cycle to measure against and only the
  // floor applies — the missing-required-fields list names the frequency itself.
  if (!cycleDays) {
    return gracePeriodDays < 1 ? "Grace period must be at least 1 day" : null;
  }

  if (gracePeriodDays >= 1 && gracePeriodDays <= cycleDays) {
    return null;
  }

  // A daily cycle leaves exactly one legal value, so the message names it.
  return cycleDays === 1
    ? "Grace period cannot be less than or greater than 1"
    : `Grace period must be between 1 and the ${frequency} verification cycle (${cycleDays} days)`;
};

/**
 * @description Validates the parts of the form the endpoint would otherwise
 * reject with a 422, so the author sees the problem next to the field.
 * @param policyConfigurationData The current form state.
 * @returns A map of field name to error message; empty when the form is valid.
 */
export const validatePolicyConfiguration = (policyConfigurationData) => {
  const errors = {};

  if (!`${policyConfigurationData?.name || ""}`.trim()) {
    errors.name = "Policy name is required";
  }

  // Each policy type requires only its own scope key — the other filter is hidden.
  if (policyConfigurationData?.policy_type === "restaurant") {
    if (!policyConfigurationData?.restaurant_ids?.length) {
      errors.restaurant_ids = "Select at least one restaurant";
    }
  } else if (!policyConfigurationData?.restaurant_group_id) {
    errors.restaurant_group_id = "Restaurant group is required";
  }

  // Everything below is a rule check, which the endpoint only applies to a rule set
  // it is being sent — see replacesPolicyRuleSet.
  if (!replacesPolicyRuleSet(policyConfigurationData)) {
    return errors;
  }

  if (policyConfigurationData?.exceptions_permitted === true
    && !policyConfigurationData?.exception_network_guest_ids?.length) {
    errors.exception_network_guest_ids = "Select at least one user to except";
  }

  if (policyConfigurationData?.inactivity_rule_enabled) {
    if (!policyConfigurationData?.inactivity_checks?.length) {
      errors.inactivity_checks = "Select at least one activity signal";
    }

    if (!(Number(policyConfigurationData?.inactivity_period_days) > 0)) {
      errors.inactivity_period_days = "Inactivity period must be greater than 0";
    }
  }

  const gracePeriodError = describeGracePeriodError(
    isIntegerFieldSet(policyConfigurationData?.grace_period_days)
      ? Number(policyConfigurationData.grace_period_days)
      : null,
    policyConfigurationData?.verification_frequency
  );

  if (gracePeriodError) {
    errors.grace_period_days = gracePeriodError;
  }

  return errors;
};

export const ACTIVE_POLICY_CHECKING_TEXT = "Checking for active policy...";
export const ACTIVE_POLICY_CLEAR_TEXT = "No active policy found you can proceed to create";

const RuleStatusLabel = ({ isSet, ruleKey }) => (
  <RuleStatus isSet={isSet} data-testid={`policy-engine-rule-status-${ruleKey}`}>
    {isSet ? "Set" : "Not set"}
  </RuleStatus>
);

export const PolicyEngineConfigurationModal = ({
  open,
  onClose,
  policyConfigurationData,
  setPolicyConfigurationData,
  onSubmit,
  onValidationError,
  isSubmitting = false,
  mode = "create",
  isLoading = false,
  restaurantGroupDropdown = {},
  restaurantDropdown = {},
  manualUsersDropdown = {},
  activePolicyCheck = {},
}) => {
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) {
      setErrors({});
    }
  }, [open]);

  const isEditMode = mode === "edit";
  const isRestaurantPolicy = policyConfigurationData?.policy_type === "restaurant";

  const updateField = (field, value) => {
    setPolicyConfigurationData((currentData) => ({ ...currentData, [field]: value }));
    setErrors((currentErrors) => ({ ...currentErrors, [field]: undefined }));
  };

  // Both scope selections are kept when the type is toggled — buildPolicyPayload
  // only ever sends the key matching the chosen type, so the hidden one cannot
  // leak into the request, and a mis-click does not discard the author's work.
  // Only the scope errors are dropped, since they no longer apply.
  const handlePolicyTypeChange = (policyType) => {
    if (policyType === policyConfigurationData?.policy_type) {
      return;
    }

    setPolicyConfigurationData((currentData) => ({ ...currentData, policy_type: policyType }));
    setErrors((currentErrors) => ({
      ...currentErrors,
      restaurant_group_id: undefined,
      restaurant_ids: undefined,
    }));
  };

  const handleRestaurantGroupChange = (event) => {
    updateField("restaurant_group_id", event.target.value);
  };

  // Switching exceptions off drops the selection: the endpoint rejects a list
  // without the switch, so leaving it behind would make the next save fail.
  const handleExceptionsPermittedChange = (isPermitted) => {
    setPolicyConfigurationData((currentData) => ({
      ...currentData,
      exceptions_permitted: isPermitted,
      exception_network_guest_ids: isPermitted ? currentData?.exception_network_guest_ids || [] : [],
    }));
    setErrors((currentErrors) => ({
      ...currentErrors,
      exceptions_permitted: undefined,
      exception_network_guest_ids: undefined,
    }));
  };

  const handleInactivityCheckToggle = (checkValue) => {
    const currentChecks = policyConfigurationData?.inactivity_checks || [];
    const nextChecks = currentChecks.includes(checkValue)
      ? currentChecks.filter((check) => check !== checkValue)
      : [...currentChecks, checkValue];

    updateField("inactivity_checks", nextChecks);
  };

  const handleAddInactivityRule = () => {
    setPolicyConfigurationData((currentData) => ({
      ...currentData,
      inactivity_rule_enabled: true,
      inactivity_checks: currentData?.inactivity_checks?.length
        ? currentData.inactivity_checks
        : DEFAULT_INACTIVITY_CHECKS,
      inactivity_period_days: isIntegerFieldSet(currentData?.inactivity_period_days)
        ? currentData.inactivity_period_days
        : DEFAULT_INACTIVITY_PERIOD_DAYS,
    }));

    setErrors((currentErrors) => ({
      ...currentErrors,
      inactivity_checks: undefined,
      inactivity_period_days: undefined,
    }));
  };

  const isInactivityReviewGroupActive = hasAnyInactivityReviewField(policyConfigurationData);

  const handleRemoveInactivityRule = () => {
    setPolicyConfigurationData((currentData) => ({
      ...currentData,
      inactivity_rule_enabled: false,
      inactivity_checks: [],
      inactivity_period_days: "",
    }));
    setErrors((currentErrors) => ({
      ...currentErrors,
      inactivity_checks: undefined,
      inactivity_period_days: undefined,
    }));
  };

  const handleSubmit = () => {
    const validationErrors = validatePolicyConfiguration(policyConfigurationData);
    // The rule controls carry no message of their own — only an outline — so this
    // list is what actually names them, and a control scrolled out of view is
    // invisible until the snackbar does.
    const missingRequiredFields = collectMissingRequiredPolicyFields(policyConfigurationData);

    if (Object.keys(validationErrors).length || missingRequiredFields.length) {
      // A blank member of a required group carries no message of its own, so it gets
      // a generic one — enough to outline its control. The field-level messages are
      // more specific, so they win wherever both apply.
      setErrors({
        ...Object.fromEntries(
          missingRequiredFields.map((field) => [field, `${POLICY_FIELD_LABELS[field]} is required`])
        ),
        ...validationErrors,
      });
      onValidationError?.({
        missingFields: missingRequiredFields.map((field) => POLICY_FIELD_LABELS[field]),
        // A rule broken in some way other than being blank — a grace period outside
        // the window its cycle allows, a zero inactivity period — is named nowhere else.
        ruleMessages: Object.entries(validationErrors)
          .filter(([field]) => (
            !INLINE_MESSAGE_FIELDS.includes(field) && !missingRequiredFields.includes(field)
          ))
          .map(([, message]) => message),
      });
      return;
    }

    setErrors({});
    onSubmit?.(
      isEditMode
        ? buildPolicyUpdatePayload(policyConfigurationData)
        : buildPolicyPayload(policyConfigurationData)
    );
  };

  // In edit mode the scope is locked, and its name is handed in already resolved
  // by the listing — the create-scoped dropdowns are not even queried, so their
  // caches cannot be relied on to hold the policy's own scope.
  const getRestaurantGroupName = (restaurantGroupId) => {
    if (isEditMode) {
      return policyConfigurationData?.scope_name || "";
    }

    const restaurantGroup = restaurantGroupDropdown?.optionsCache?.find(
      (option) => String(option?.id) === String(restaurantGroupId)
    );
    return restaurantGroup?.name || "";
  };

  const getSelectedRestaurantNames = () => {
    if (isEditMode) {
      return policyConfigurationData?.scope_name || "";
    }

    const selectedIds = policyConfigurationData?.restaurant_ids || [];
    const names = selectedIds.map((restaurantId) => {
      const restaurant = restaurantDropdown?.optionsCache?.find(
        (option) => String(option?.id) === String(restaurantId)
      );
      return restaurant?.name || restaurantId;
    });

    return names.join(", ");
  };

  // Both lists are consulted: the cache accumulates across pages and searches,
  // while `options` holds what is on screen right now — a user just clicked is in
  // the latter even if the cache effect has not flushed yet.
  const findManualUserName = (guestId) => {
    const matches = (option) => String(option?.id) === String(guestId);
    const guest = manualUsersDropdown?.optionsCache?.find(matches)
      || manualUsersDropdown?.options?.find(matches);

    return guest?.name;
  };

  // One selection reads as the user's name; several collapse to a count, since a
  // joined list of names overflows the field long before it stays legible.
  const getSelectedExceptionUsersLabel = () => {
    const selectedIds = policyConfigurationData?.exception_network_guest_ids || [];

    if (selectedIds.length > 1) {
      return `${selectedIds.length} selected`;
    }

    // Falls back to the raw id only when the name is genuinely unresolvable, so a
    // selection is never silently invisible.
    return selectedIds.length ? findManualUserName(selectedIds[0]) || `${selectedIds[0]}` : "";
  };

  const isDraft = Boolean(policyConfigurationData?.draft);
  const isActivePolicyCheckRequired = !isEditMode && !isDraft;
  const areRulesDisabled = isActivePolicyCheckRequired && !activePolicyCheck?.isClear;
  const hasSelectedScope = isRestaurantPolicy
    ? !!policyConfigurationData?.restaurant_ids?.length
    : !!policyConfigurationData?.restaurant_group_id;
  // Saving is blocked from the moment a scope is picked until its check comes back
  // clear. With nothing picked the action stays live on purpose: it cannot produce a
  // request either way, and it is what surfaces the "select a scope" validation.
  const isSubmitBlockedByActivePolicy = isActivePolicyCheckRequired && hasSelectedScope && !activePolicyCheck?.isClear;
  // Creating names what the save produces (a draft or a live policy); updating
  // names the action itself, since the draft toggle is only one of its effects.
  const submitLabel = isEditMode ? "Update Policy" : (isDraft ? "Draft Policy" : "Create Policy");
  const hasExceptionsPermittedValue =
    policyConfigurationData?.exceptions_permitted !== null
    && policyConfigurationData?.exceptions_permitted !== undefined;
  const areExceptionsPermitted = policyConfigurationData?.exceptions_permitted === true;

  return (
    <Dialog
      data-testid="policy-engine-configuration-modal"
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={isLoading ? LoadingPolicyDialogPaperProps : PolicyDialogPaperProps}
    >
      <TitleContainer>
        <TitleText data-testid="policy-engine-modal-title">
          {isEditMode ? "Edit Policy" : "Create Policy"}
        </TitleText>
        <CloseIconButton
          data-testid="policy-engine-modal-close-btn"
          onClick={onClose}
          disableRipple
        >
          <ClearIcon fontSize="small" />
        </CloseIconButton>
      </TitleContainer>

      {/* Edit only: the form is populated from the fetched policy, so the
          loader stands in for it rather than fields filling in afterwards. */}
      {isLoading ? (
        <ModalLoaderContainer data-testid="policy-engine-modal-loader">
          <Loader />
        </ModalLoaderContainer>
      ) : (
        <DialogContent sx={scrollableContent}>
          {/* Draft */}
          <SectionContainer>
            <DraftBanner isDraft={isDraft}>
              <Box sx={{ display: "flex", gap: "10px" }}>
                <DraftIconWrapper isDraft={isDraft}>
                  {isDraft ? <DescriptionOutlinedIcon /> : <TaskAltIcon />}
                </DraftIconWrapper>
                <Box>
                  <DraftTitle>Draft</DraftTitle>
                  <DraftHelperText>
                    {isDraft
                      ? "Saved but not enforced. Turn off to make this policy live."
                      : "Off — this policy takes effect as soon as it's saved."}
                  </DraftHelperText>
                </Box>
              </Box>
              <StyledSwitch
                data-testid="policy-engine-draft-switch"
                checked={isDraft}
                onChange={(event) => updateField("draft", event.target.checked)}
              />
            </DraftBanner>

            <FieldContainer sx={{ marginTop: "16px" }}>
              <FieldLabel>Policy name<RequiredMark>*</RequiredMark></FieldLabel>
              <StyledTextField
                inputProps={{ "data-testid": "policy-engine-name-input" }}
                fullWidth
                size="small"
                placeholder="Executive Policy"
                value={policyConfigurationData?.name || ""}
                onChange={(event) => updateField("name", event.target.value)}
                error={!!errors?.name}
                autoComplete="off"
              />
              {!!errors?.name && <ErrorText>{errors.name}</ErrorText>}
            </FieldContainer>

            {/* Locked while editing: the update endpoint takes neither the policy
                type nor a scope, so a policy keeps the scope it was created with. */}
            <FieldContainer>
              <FieldLabel>Policy type<RequiredMark>*</RequiredMark></FieldLabel>
              <PolicyTypeGroup>
                {POLICY_TYPE_OPTIONS.map((option) => (
                  <PolicyTypeOption
                    key={option.value}
                    data-testid={`policy-engine-policy-type-${option.value}`}
                    disableRipple
                    disabled={isEditMode}
                    isSelected={policyConfigurationData?.policy_type === option.value}
                    onClick={() => handlePolicyTypeChange(option.value)}
                  >
                    {option.label}
                  </PolicyTypeOption>
                ))}
              </PolicyTypeGroup>
            </FieldContainer>

            {/* The two scopes are mutually exclusive: a group policy is scoped by its
                group, a restaurant policy by its restaurants. Only one filter shows. */}
            {!isRestaurantPolicy && (
              <FieldContainer>
                <FieldLabel>Restaurant group<RequiredMark>*</RequiredMark></FieldLabel>
                <FormControl fullWidth size="small">
                  <StyledSelect
                    data-testid="policy-engine-modal-restaurant-group-select"
                    size="small"
                    displayEmpty
                    disabled={isEditMode}
                    value={policyConfigurationData?.restaurant_group_id || ""}
                    renderValue={() => {
                      const name = getRestaurantGroupName(policyConfigurationData?.restaurant_group_id);
                      return name || <SelectPlaceholder>Select a group...</SelectPlaceholder>;
                    }}
                    onChange={handleRestaurantGroupChange}
                    onClose={restaurantGroupDropdown?.onMenuClose}
                    error={!!errors?.restaurant_group_id}
                    MenuProps={dropdownMenuProps(restaurantGroupDropdown?.menuRef)}
                  >
                    <DropdownSearchBar
                      changeSearchText={restaurantGroupDropdown?.onSearchChange}
                      placeholder="Search restaurant group"
                      isFetching={restaurantGroupDropdown?.isFetching}
                    />
                    {restaurantGroupDropdown?.options?.map((option) => (
                      <StyledMenuItem key={option?.id} value={option?.id}>
                        {String(policyConfigurationData?.restaurant_group_id) === String(option?.id)
                          ? <SelectedMenuItem data={option?.name} />
                          : option?.name}
                      </StyledMenuItem>
                    ))}
                    <DropdownInfiniteLoader
                      hasNextPage={restaurantGroupDropdown?.hasNextPage}
                      targetRef={restaurantGroupDropdown?.loaderRef}
                    />
                  </StyledSelect>
                </FormControl>
                {!!errors?.restaurant_group_id && <ErrorText>{errors.restaurant_group_id}</ErrorText>}
              </FieldContainer>
            )}

            {isRestaurantPolicy && (
              <FieldContainer>
                <FieldLabel>Restaurant<RequiredMark>*</RequiredMark></FieldLabel>
                <FormControl fullWidth size="small">
                  <StyledSelect
                    data-testid="policy-engine-modal-restaurant-select"
                    size="small"
                    multiple
                    displayEmpty
                    disabled={isEditMode}
                    value={policyConfigurationData?.restaurant_ids || []}
                    renderValue={() => {
                      const names = getSelectedRestaurantNames();
                      return names || <SelectPlaceholder>Select a restaurant...</SelectPlaceholder>;
                    }}
                    onChange={(event) => updateField("restaurant_ids", event.target.value)}
                    onClose={restaurantDropdown?.onMenuClose}
                    error={!!errors?.restaurant_ids}
                    MenuProps={dropdownMenuProps(restaurantDropdown?.menuRef)}
                  >
                    <DropdownSearchBar
                      changeSearchText={restaurantDropdown?.onSearchChange}
                      placeholder="Search restaurant"
                      isFetching={restaurantDropdown?.isFetching}
                    />
                    {restaurantDropdown?.options?.map((option) => (
                      <StyledMenuItem key={option?.id} value={option?.id}>
                        {policyConfigurationData?.restaurant_ids?.includes(option?.id)
                          ? <SelectedMenuItem data={option?.name} />
                          : option?.name}
                      </StyledMenuItem>
                    ))}
                    <DropdownInfiniteLoader
                      hasNextPage={restaurantDropdown?.hasNextPage}
                      targetRef={restaurantDropdown?.loaderRef}
                    />
                  </StyledSelect>
                </FormControl>
                {!!errors?.restaurant_ids && <ErrorText>{errors.restaurant_ids}</ErrorText>}
                <HelperText>Fields left unset inherit from the parent policy.</HelperText>
              </FieldContainer>
            )}

            {/* Outcome of the check on the selected scope. A conflict is reported
                in the snackbar by the page, so only these two states show here. */}
            {isActivePolicyCheckRequired && !!activePolicyCheck?.isChecking && (
              <ActivePolicyCheckText data-testid="policy-engine-active-policy-checking">
                {ACTIVE_POLICY_CHECKING_TEXT}
              </ActivePolicyCheckText>
            )}
            {isActivePolicyCheckRequired && !activePolicyCheck?.isChecking && !!activePolicyCheck?.isClear && (
              <ActivePolicyCheckText isClear data-testid="policy-engine-active-policy-clear">
                {ACTIVE_POLICY_CLEAR_TEXT}
              </ActivePolicyCheckText>
            )}

            <FieldContainer sx={{ marginBottom: 0 }}>
              <FieldLabel>Description</FieldLabel>
              <StyledTextField
                inputProps={{ "data-testid": "policy-engine-description-input" }}
                fullWidth
                size="small"
                multiline
                rows={3}
                placeholder="Description of this policy"
                value={policyConfigurationData?.description || ""}
                onChange={(event) => updateField("description", event.target.value)}
              />
            </FieldContainer>
          </SectionContainer>

          {/* Effective rules */}
          <SectionContainer>
            <SectionTitle>Effective rules</SectionTitle>
            <SectionHelperText>Fields you set here override the parent group policy.</SectionHelperText>

            <RuleRow>
              <RuleLabelContainer>
                <RuleIconWrapper><PersonOutlineIcon /></RuleIconWrapper>
                <RuleLabel>Applies to<RequiredMark>*</RequiredMark></RuleLabel>
              </RuleLabelContainer>
              <RuleControlContainer>
                <FormControl size="small" sx={{ width: "150px" }}>
                  <StyledSelect
                    data-testid="policy-engine-applies-to-select"
                    size="small"
                    disabled
                    value={policyConfigurationData?.verification_method || "manual"}
                  >
                    {APPLIES_TO_OPTIONS.map((option) => (
                      <StyledMenuItem key={option.value} value={option.value}>
                        {option.label}
                      </StyledMenuItem>
                    ))}
                  </StyledSelect>
                </FormControl>
                <RuleStatusLabel isSet={!!policyConfigurationData?.verification_method} ruleKey="verification_method" />
              </RuleControlContainer>
            </RuleRow>

            <RuleRow>
              <RuleLabelContainer>
                <RuleIconWrapper><VerifiedUserOutlinedIcon /></RuleIconWrapper>
                <RuleLabel>Verification method{isInactivityReviewGroupActive && <RequiredMark>*</RequiredMark>}</RuleLabel>
              </RuleLabelContainer>
              <RuleControlContainer>
                <FormControl size="small" sx={{ width: "150px" }}>
                  <StyledSelect
                    data-testid="policy-engine-notification-recipients-select"
                    size="small"
                    displayEmpty
                    disabled={areRulesDisabled}
                    value={policyConfigurationData?.notification_recipients || ""}
                    renderValue={(selected) => {
                      const option = NOTIFICATION_RECIPIENT_OPTIONS.find((item) => item.value === selected);
                      return option?.label || "Inherit";
                    }}
                    onChange={(event) => updateField("notification_recipients", event.target.value)}
                    error={!!errors?.notification_recipients}
                  >
                    <StyledMenuItem value="">Inherit</StyledMenuItem>
                    {NOTIFICATION_RECIPIENT_OPTIONS.map((option) => (
                      <StyledMenuItem key={option.value} value={option.value}>
                        {policyConfigurationData?.notification_recipients === option.value
                          ? <SelectedMenuItem data={option.label} />
                          : option.label}
                      </StyledMenuItem>
                    ))}
                  </StyledSelect>
                </FormControl>
                <RuleStatusLabel isSet={!!policyConfigurationData?.notification_recipients} ruleKey="notification_recipients" />
              </RuleControlContainer>
            </RuleRow>

            <RuleRow>
              <RuleLabelContainer>
                <RuleIconWrapper><AccessTimeIcon /></RuleIconWrapper>
                <RuleLabel>Verification frequency{isInactivityReviewGroupActive && <RequiredMark>*</RequiredMark>}</RuleLabel>
              </RuleLabelContainer>
              <RuleControlContainer>
                <FormControl size="small" sx={{ width: "150px" }}>
                  <StyledSelect
                    data-testid="policy-engine-verification-frequency-select"
                    size="small"
                    displayEmpty
                    disabled={areRulesDisabled}
                    value={policyConfigurationData?.verification_frequency || ""}
                    renderValue={(selected) => {
                      const option = VERIFICATION_FREQUENCY_OPTIONS.find((item) => item.value === selected);
                      return option?.label || "Inherit";
                    }}
                    MenuProps={{
                      PaperProps: { sx: { width: "200px" } },
                    }}
                    onChange={(event) => updateField("verification_frequency", event.target.value)}
                    error={!!errors?.verification_frequency}
                  >
                    <StyledMenuItem value="">Inherit</StyledMenuItem>
                    {VERIFICATION_FREQUENCY_OPTIONS.map((option) => (
                      <StyledMenuItem key={option.value} value={option.value}>
                        {policyConfigurationData?.verification_frequency === option.value
                          ? <SelectedMenuItem data={option.label} />
                          : option.label}
                      </StyledMenuItem>
                    ))}
                  </StyledSelect>
                </FormControl>
                <RuleStatusLabel isSet={!!policyConfigurationData?.verification_frequency} ruleKey="verification_frequency" />
              </RuleControlContainer>
            </RuleRow>

            <RuleRow>
              <RuleLabelContainer>
                <RuleIconWrapper><PeopleOutlineIcon /></RuleIconWrapper>
                <RuleLabel>Max active users</RuleLabel>
              </RuleLabelContainer>
              <RuleControlContainer>
                <StyledTextField
                  size="small"
                  sx={{ width: "150px" }}
                  placeholder="100"
                  disabled={areRulesDisabled}
                  inputProps={{ inputMode: "numeric", "data-testid": "policy-engine-max-active-users-input" }}
                  value={policyConfigurationData?.max_active_users ?? ""}
                  onChange={(event) => updateField("max_active_users", sanitizeIntegerInput(event.target.value))}
                />
                <RuleStatusLabel isSet={isIntegerFieldSet(policyConfigurationData?.max_active_users)} ruleKey="max_active_users" />
              </RuleControlContainer>
            </RuleRow>

            <RuleRow>
              <RuleLabelContainer>
                <RuleIconWrapper><ErrorOutlineIcon /></RuleIconWrapper>
                <RuleLabel>Exceptions permitted</RuleLabel>
              </RuleLabelContainer>
              <RuleControlContainer>
                <Box sx={{ alignItems: "center", display: "flex", gap: "4px", width: "150px" }}>
                  <StyledSwitch
                    data-testid="policy-engine-exceptions-permitted-switch"
                    disabled={areRulesDisabled}
                    checked={policyConfigurationData?.exceptions_permitted === true}
                    onChange={(event) => handleExceptionsPermittedChange(event.target.checked)}
                  />
                  <ToggleValueText>
                    {policyConfigurationData?.exceptions_permitted === true ? "Allowed" : "Not allowed"}
                  </ToggleValueText>
                </Box>
                <RuleStatusLabel isSet={hasExceptionsPermittedValue} ruleKey="exceptions_permitted" />
              </RuleControlContainer>
            </RuleRow>

            <RuleRow>
              <RuleLabelContainer>
                <RuleIconWrapper><PersonOutlineIcon /></RuleIconWrapper>
                <RuleLabel>Excepted users{areExceptionsPermitted && <RequiredMark>*</RequiredMark>}</RuleLabel>
              </RuleLabelContainer>
              <RuleControlContainer>
                <Box sx={{ width: "150px" }}>
                  <FormControl fullWidth size="small">
                    <StyledSelect
                      data-testid="policy-engine-exception-users-select"
                      size="small"
                      multiple
                      displayEmpty
                      disabled={areRulesDisabled || !areExceptionsPermitted}
                      value={policyConfigurationData?.exception_network_guest_ids || []}
                      renderValue={() => {
                        const label = getSelectedExceptionUsersLabel();
                        return label || <SelectPlaceholder>Select users</SelectPlaceholder>;
                      }}
                      onChange={(event) => updateField("exception_network_guest_ids", event.target.value)}
                      onClose={manualUsersDropdown?.onMenuClose}
                      error={!!errors?.exception_network_guest_ids}
                      MenuProps={dropdownMenuProps(manualUsersDropdown?.menuRef)}
                    >
                      <DropdownSearchBar
                        changeSearchText={manualUsersDropdown?.onSearchChange}
                        placeholder="Search user"
                        isFetching={manualUsersDropdown?.isFetching}
                      />
                      {manualUsersDropdown?.options?.map((option) => (
                        <StyledMenuItem key={option?.id} value={option?.id}>
                          {policyConfigurationData?.exception_network_guest_ids?.includes(option?.id)
                            ? <SelectedMenuItem data={option?.name} />
                            : option?.name}
                        </StyledMenuItem>
                      ))}
                      <DropdownInfiniteLoader
                        hasNextPage={manualUsersDropdown?.hasNextPage}
                        targetRef={manualUsersDropdown?.loaderRef}
                      />
                    </StyledSelect>
                  </FormControl>
                </Box>
                <RuleStatusLabel
                  isSet={!!policyConfigurationData?.exception_network_guest_ids?.length}
                  ruleKey="exception_network_guest_ids"
                />
              </RuleControlContainer>
            </RuleRow>
            <RuleRow>
              <RuleLabelContainer>
                <RuleIconWrapper><HourglassEmptyIcon /></RuleIconWrapper>
                <RuleLabel>Grace period (days){isInactivityReviewGroupActive && <RequiredMark>*</RequiredMark>}</RuleLabel>
              </RuleLabelContainer>
              <RuleControlContainer>
                <Box sx={{ width: "150px" }}>
                  <StyledTextField
                    fullWidth
                    size="small"
                    placeholder="7"
                    disabled={areRulesDisabled}
                    inputProps={{ inputMode: "numeric", "data-testid": "policy-engine-grace-period-input" }}
                    value={policyConfigurationData?.grace_period_days ?? ""}
                    onChange={(event) => updateField("grace_period_days", sanitizeIntegerInput(event.target.value))}
                    error={!!errors?.grace_period_days}
                  />
                </Box>
                <RuleStatusLabel isSet={isIntegerFieldSet(policyConfigurationData?.grace_period_days)} ruleKey="grace_period_days" />
              </RuleControlContainer>
            </RuleRow>
          </SectionContainer>

          {/* Inactivity rules */}
          <InactivitySection>
            <InactivityHeader>
              <Box>
                <SectionTitle>
                  Inactivity rules{isInactivityReviewGroupActive && <RequiredMark>*</RequiredMark>}
                </SectionTitle>
                <SectionHelperText sx={{ marginBottom: 0 }}>
                  What counts as activity for a user.
                </SectionHelperText>
              </Box>
              {!policyConfigurationData?.inactivity_rule_enabled && (
                <AddRuleButton
                  data-testid="policy-engine-add-inactivity-rule-btn"
                  variant="outlined"
                  disableRipple
                  disabled={areRulesDisabled}
                  startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
                  onClick={handleAddInactivityRule}
                >
                  Add rule
                </AddRuleButton>
              )}
            </InactivityHeader>

            {!!policyConfigurationData?.inactivity_rule_enabled && (
              <InactivityRuleCard data-testid="policy-engine-inactivity-rule-card">
                <Box>
                  <InactivityRuleCardHeader>
                    <InactivityFieldLabel sx={{ marginBottom: 0 }}>
                      Counts as activity{isInactivityReviewGroupActive && <RequiredMark>*</RequiredMark>}
                    </InactivityFieldLabel>
                    <CloseIconButton
                      data-testid="policy-engine-remove-inactivity-rule-btn"
                      onClick={handleRemoveInactivityRule}
                      disableRipple
                      disabled={areRulesDisabled}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </CloseIconButton>
                  </InactivityRuleCardHeader>
                  <ActivityChipGroup>
                    {INACTIVITY_CHECK_OPTIONS.map((option) => {
                      const isSelected = policyConfigurationData?.inactivity_checks?.includes(option.value);

                      return (
                        <ActivityChip
                          key={option.value}
                          data-testid={`policy-engine-inactivity-check-${option.value}`}
                          isSelected={isSelected}
                          hasError={!!errors?.inactivity_checks}
                          sx={areRulesDisabled ? { cursor: "not-allowed", opacity: 0.5 } : undefined}
                          onClick={() => !areRulesDisabled && handleInactivityCheckToggle(option.value)}
                        >
                          <ActivityChipCheckbox
                            checked={Boolean(isSelected)}
                            disabled={areRulesDisabled}
                            disableRipple
                            tabIndex={-1}
                            inputProps={{ "aria-label": option.label }}
                          />
                          {option.label}
                        </ActivityChip>
                      );
                    })}
                  </ActivityChipGroup>                </Box>

                <InactivityPeriodContainer>
                  <InactivityFieldLabel>Inactivity period{isInactivityReviewGroupActive && <RequiredMark>*</RequiredMark>}</InactivityFieldLabel>
                  <InactivityPeriodRow>
                    <StyledTextField
                      size="small"
                      sx={{ width: "80px" }}
                      placeholder="30"
                      disabled={areRulesDisabled}
                      inputProps={{ inputMode: "numeric", "data-testid": "policy-engine-inactivity-period-input" }}
                      value={policyConfigurationData?.inactivity_period_days ?? ""}
                      onChange={(event) => updateField("inactivity_period_days", sanitizeIntegerInput(event.target.value))}
                      error={!!errors?.inactivity_period_days}
                    />
                    <InactivityPeriodUnit>days</InactivityPeriodUnit>
                  </InactivityPeriodRow>                </InactivityPeriodContainer>
              </InactivityRuleCard>
            )}
          </InactivitySection>
        </DialogContent>
      )}

      <FixedActionContainer>
        <ModalCancelButton
          data-testid="policy-engine-modal-cancel-btn"
          disableRipple
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </ModalCancelButton>
        <CreateButton
          data-testid="policy-engine-modal-create-btn"
          disableRipple
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || isLoading || isSubmitBlockedByActivePolicy}
        >
          {isSubmitting ? <LoadingSpinner isLoading={true} /> : submitLabel}
        </CreateButton>
      </FixedActionContainer>
    </Dialog>
  );
};
